import * as React from "react"
import { Link, useRouteMatch } from "react-router-dom"
import { LoggedRedirect } from "@digits-shared/components/Router/LoggedRedirect"
import { SvgDigitsLogoGreen } from "@digits-shared/components/SVGIcons/digits/DigitsLogoGreen.svg"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled, { createGlobalStyle } from "styled-components"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import { useMobileRoutes } from "src/frontend/components/Shared/Responsive/mobileRoutes"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"

/*
  STYLES
*/

// Note: main#main has increased specificity to take precedence over
// app-level global styles
const BodyStyles = createGlobalStyle`
  html, body, main#main {
    max-width: 100vw;
    min-width: 100vw;
    width: 100vw;
    max-height: -webkit-fill-available;
    min-height: -webkit-fill-available;
    height: -webkit-fill-available;
    overflow: hidden;
  }

  main#main {
    padding: 0;
  }
`

const MobileStyled = styled.div`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 20px;
  background: ${colors.secondary} url(${require("static/images/onboard/background.png")}) no-repeat
    center;
  background-size: cover;
  color: ${colors.white};
`

export const BlockMobileHeadline = styled.div`
  text-align: center;
  font-size: 26px;
  line-height: 34px;
  margin-bottom: 30px;
  font-weight: ${fonts.weight.book};

  strong {
    font-weight: ${fonts.weight.medium};
  }
`

export const BlockMobileIconContainer = styled.div`
  position: relative;
  margin: -10% auto 40px;
  width: 100%;
  height: 110px;
`

const Message = styled.div`
  font-size: 16px;
  text-align: center;
  line-height: 19px;
  max-width: 250px;
  margin: 0 auto;
`

/*
  COMPONENTS
*/

export const BlockMobile: React.FC = () => {
  const generatePath = useFrontendPathGenerator()
  const isLegalEntityRoot = useRouteMatch(routes.legalEntity.parameterizedPath)
  const mobileRoutes = useMobileRoutes()
  const { currentLegalEntity } = useSession<FrontendSession>()
  const { viewVersion } = useViewVersion()

  const mobileHome =
    viewVersion && currentLegalEntity?.slug
      ? generatePath(mobileRoutes.home, { leSlug: currentLegalEntity?.slug })
      : undefined

  if (isLegalEntityRoot?.isExact && mobileHome) {
    return <LoggedRedirect name="useBlockMobile-mobileHome" to={mobileHome} />
  }

  return (
    <MobileStyled>
      <BodyStyles />
      <BlockMobileIconContainer>
        <SvgDigitsLogoGreen />
      </BlockMobileIconContainer>
      <BlockMobileHeadline>
        This page is not optimized for <strong>mobile visualization.</strong>
      </BlockMobileHeadline>
      <Message>Please visit us from your desktop to see this content.</Message>

      {mobileHome && (
        <Message>
          <br />
          <br />
          Or,{" "}
          <Link to={mobileHome}>
            go to <b>Mobile Dashboard</b>
          </Link>
          .
        </Message>
      )}
    </MobileStyled>
  )
}
